/**
 * #1330 — Summary accordion scope for Facility Power family steps.
 */

import {
  shouldFoldFacilityPowerChildSectionsInline,
  shouldShowPowerDistributionSection,
} from './facilityPowerScopeSections';
import {
  resolveFacilityPowerFamilyProgressFields,
  type FacilityPowerFamilyStepId,
} from './facilityPowerStepProgress';
import { filterSubsectionFieldsForFormView } from './formFieldVisibility';
import { countSubsectionFields } from './subsectionHeaderMetrics';
import { FACILITY_POWER_FIELDS } from '../request/fields/steps/facility-power.fields';

import type { FormFieldVisibilityContext } from './formFieldVisibility';
import type { RequestFormStepId } from '../config/requestFormStepRegistry';
import type { SummaryCardRow } from '../summary/summaryCardSourceTypes';

export const FACILITY_POWER_FAMILY_STEP_IDS = [
  'facility-power',
  'large-ups',
  'power-distribution',
] as const satisfies readonly RequestFormStepId[];

export type FacilityPowerSummaryAccordionKey =
  | 'facilityPower'
  | 'largeUps'
  | 'powerDistribution';

const DATA_FIELD_TO_SUBSECTION = new Map(
  FACILITY_POWER_FIELDS.map(field => [field.dataField, field.subsectionId]),
);

export function isFacilityPowerFamilyStepEnabled(
  enabledStepIds: readonly string[],
  stepId?: RequestFormStepId,
): boolean {
  if (stepId) {
    return enabledStepIds.includes(stepId);
  }
  return FACILITY_POWER_FAMILY_STEP_IDS.some(id => enabledStepIds.includes(id));
}

function subsectionForRow(row: SummaryCardRow): string | undefined {
  return DATA_FIELD_TO_SUBSECTION.get(row.dataField);
}

function shouldIncludeAdditionalInformationSubsection(options: {
  accordionKey: FacilityPowerSummaryAccordionKey;
  enabledStepIds: readonly string[];
  stage?: string | null;
}): boolean {
  if (options.accordionKey === 'facilityPower') {
    return options.enabledStepIds.includes('facility-power');
  }
  if (options.accordionKey === 'largeUps') {
    return options.enabledStepIds.includes('large-ups');
  }
  if (options.accordionKey === 'powerDistribution') {
    return (
      options.enabledStepIds.includes('power-distribution') &&
      shouldShowPowerDistributionSection({ stage: options.stage })
    );
  }
  return false;
}

/** Which facility-power Summary accordions to render for the active scope. */
export function resolveFacilityPowerSummaryAccordions(
  enabledStepIds: readonly string[],
): FacilityPowerSummaryAccordionKey[] {
  const accordions: FacilityPowerSummaryAccordionKey[] = [];
  if (enabledStepIds.includes('facility-power')) {
    accordions.push('facilityPower');
  }
  if (enabledStepIds.includes('large-ups')) {
    accordions.push('largeUps');
  }
  if (enabledStepIds.includes('power-distribution')) {
    accordions.push('powerDistribution');
  }
  return accordions;
}

export function filterFacilityPowerSummaryRows(
  rows: readonly SummaryCardRow[],
  options: {
    accordionKey: FacilityPowerSummaryAccordionKey;
    enabledStepIds: readonly string[];
    storedStepIds?: string[] | null;
    stage?: string | null;
    /** When true, include Power Distribution rows even in DRAFT (for counts / empty-state copy). */
    ignoreStageGate?: boolean;
  },
): SummaryCardRow[] {
  if (
    !options.ignoreStageGate &&
    options.accordionKey === 'powerDistribution' &&
    !shouldShowPowerDistributionSection({ stage: options.stage })
  ) {
    return [];
  }

  const hasLargeUpsStep = options.enabledStepIds.includes('large-ups');
  const hasPowerDistributionStep =
    options.enabledStepIds.includes('power-distribution');
  const foldChildSectionsInline = shouldFoldFacilityPowerChildSectionsInline({
    storedStepIds: options.storedStepIds,
    enabledStepIds: options.enabledStepIds,
  });
  const includeAdditional = shouldIncludeAdditionalInformationSubsection({
    accordionKey: options.accordionKey,
    enabledStepIds: options.enabledStepIds,
    stage: options.stage,
  });

  const allowedSubsections = new Set<string>();
  if (options.accordionKey === 'facilityPower') {
    allowedSubsections.add('facilityPower');
    if (foldChildSectionsInline && !hasLargeUpsStep) {
      allowedSubsections.add('largeUps');
    }
    if (foldChildSectionsInline && !hasPowerDistributionStep) {
      allowedSubsections.add('powerDistribution');
    }
    if (includeAdditional) {
      allowedSubsections.add('additionalInformation');
    }
  } else if (options.accordionKey === 'largeUps') {
    allowedSubsections.add('largeUps');
    if (includeAdditional) {
      allowedSubsections.add('additionalInformation');
    }
  } else {
    allowedSubsections.add('powerDistribution');
    if (includeAdditional) {
      allowedSubsections.add('additionalInformation');
    }
  }

  return rows.filter(row => {
    const subsection = subsectionForRow(row);
    return subsection != null && allowedSubsections.has(subsection);
  });
}

export const FACILITY_POWER_SUMMARY_ACCORDION_TITLES: Record<
  FacilityPowerSummaryAccordionKey,
  string
> = {
  facilityPower: 'Facility Power Readiness',
  largeUps: 'Large UPS',
  powerDistribution: 'Power Distribution',
};

function accordionKeyToStepId(
  accordionKey: FacilityPowerSummaryAccordionKey,
): FacilityPowerFamilyStepId {
  if (accordionKey === 'largeUps') {
    return 'large-ups';
  }
  if (accordionKey === 'powerDistribution') {
    return 'power-distribution';
  }
  return 'facility-power';
}

/**
 * Summary rows + counts for one facility-power family accordion — registry progress
 * fields are the source of truth (same as sidebar / step subsection badges).
 */
export function resolveFacilityPowerSummaryAccordionMetrics(options: {
  accordionKey: FacilityPowerSummaryAccordionKey;
  allRows: readonly SummaryCardRow[];
  enabledStepIds: readonly string[];
  storedStepIds?: string[] | null;
  stage?: string | null;
  data: Record<string, unknown>;
  visibility?: FormFieldVisibilityContext;
}): {
  displayRows: SummaryCardRow[];
  filled: number;
  total: number;
  isPowerDistributionStageGated: boolean;
} {
  const scopedRows = filterFacilityPowerSummaryRows(options.allRows, {
    accordionKey: options.accordionKey,
    enabledStepIds: options.enabledStepIds,
    storedStepIds: options.storedStepIds,
    stage: options.stage,
  });
  const isPowerDistributionStageGated =
    options.accordionKey === 'powerDistribution' &&
    scopedRows.length === 0 &&
    !shouldShowPowerDistributionSection({ stage: options.stage });

  const progressFields = filterSubsectionFieldsForFormView(
    resolveFacilityPowerFamilyProgressFields({
      stepId: accordionKeyToStepId(options.accordionKey),
      enabledStepIds: options.enabledStepIds,
      storedStepIds: options.storedStepIds,
      stage: options.stage,
    }),
    options.visibility,
  );

  const rowByField = new Map(scopedRows.map(row => [row.dataField, row]));
  const displayRows = progressFields
    .map(field => rowByField.get(field.key))
    .filter((row): row is SummaryCardRow => row != null);

  const { filled, total } = countSubsectionFields(progressFields, options.data);
  return { displayRows, filled, total, isPowerDistributionStageGated };
}
